import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AppStoreButton from "./AppStoreButton";
import GooglePlayButton from "./GooglePlayButton";

const NOISE_BG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

const PURPLE_MESHES = [
  "radial-gradient(ellipse 70% 60% at 20% 80%, rgba(102,73,255,0.15), transparent), radial-gradient(ellipse 50% 50% at 80% 20%, rgba(140,100,255,0.08), transparent)",
  "radial-gradient(ellipse 60% 50% at 80% 70%, rgba(102,73,255,0.15), transparent), radial-gradient(ellipse 40% 40% at 20% 30%, rgba(140,100,255,0.08), transparent)",
];

interface FeatureItem {
  id: string;
  image: string[];
  num: string;
  titleKey?: string;
  bodyKey?: string;
  hasStoreButtons?: boolean;
}

interface FeatureListProps {
  scrollRef?: React.RefObject<HTMLElement | null>;
}

export default function FeatureList({ scrollRef }: FeatureListProps) {
  const { t } = useTranslation();

  const items: FeatureItem[] = [
    {
      id: "discover",
      image: ["/flat_view_english.png", "/home_flat_preview_english.png"],
      num: "02",
    },
    {
      id: "apply",
      image: ["/video_application_english.png"],
      num: "03",
    },
    {
      id: "chat",
      image: ["/chat_english.png"],
      num: "04",
    },
    {
      id: "try_now",
      image: ["/profile_english.png"],
      num: "05",
      titleKey: "try_now_heading",
      bodyKey: "try_now_paragraph",
      hasStoreButtons: true,
    },
  ];

  const textReveal = {
    hidden: { opacity: 0, y: 36 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.12,
        ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
      },
    }),
  };

  return (
    <section ref={scrollRef} className="w-full font-gnarly-regular">
      {items.map((item, index) => {
        const isPurple = index % 2 === 0;

        return (
          <div
            key={item.id}
            className={`relative w-full min-h-screen ${
              isPurple ? "p-3 md:p-5 flex flex-col" : "bg-[#fffefd]"
            }`}
          >
            <div
              className={`relative scroll-mt-28 flex flex-col justify-center overflow-hidden ${
                isPurple
                  ? "flex-1 bg-[#6649ff] text-white rounded-2xl md:rounded-3xl"
                  : "min-h-screen text-neutral-900"
              }`}
            >
              {/* Gradient mesh overlay for purple sections */}
              {isPurple && (
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: PURPLE_MESHES[index === 0 ? 0 : 1],
                  }}
                />
              )}

              {/* Subtle radial glow for white sections */}
              {!isPurple && (
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 50% 50% at 70% 30%, rgba(102,73,255,0.04), transparent)",
                  }}
                />
              )}

              {/* Noise texture */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.03]"
                style={{
                  backgroundImage: NOISE_BG,
                  backgroundSize: "128px 128px",
                }}
              />

              {/* Content container */}
              <div className="relative mx-auto w-full max-w-7xl px-6 md:px-12 py-16 md:py-28">
                <div
                  className={`grid w-full grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center ${
                    index % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  {/* Text content with staggered reveal */}
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-15%" }}
                    className="space-y-5 md:space-y-7"
                  >
                    <motion.h2
                      custom={1}
                      variants={textReveal}
                      className={`text-3xl md:text-[3.25rem] font-gnarly-semibold leading-[1.08] tracking-tight ${
                        isPurple ? "text-white" : "text-neutral-900"
                      }`}
                    >
                      {t(item.titleKey ?? `feature_${item.id}_title`)}
                    </motion.h2>
                    <motion.p
                      custom={2}
                      variants={textReveal}
                      className={`text-base md:text-xl max-w-xl leading-relaxed ${
                        isPurple ? "text-white/70" : "text-neutral-500"
                      }`}
                    >
                      {t(item.bodyKey ?? `feature_${item.id}_body`)}
                    </motion.p>

                    {item.hasStoreButtons && (
                      <motion.div
                        custom={3}
                        variants={textReveal}
                        className="flex flex-row gap-4 pt-2"
                      >
                        <GooglePlayButton
                          className={isPurple ? "bg-white/15" : "bg-black"}
                        />
                        <AppStoreButton
                          className={isPurple ? "bg-white/15" : "bg-black"}
                        />
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Image with reveal */}
                  <motion.div
                    initial={{ opacity: 0, y: 40, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.9,
                      delay: 0.15,
                      ease: [0.16, 1, 0.3, 1] as [
                        number,
                        number,
                        number,
                        number,
                      ],
                    }}
                    viewport={{ once: true, margin: "-15%" }}
                    className="relative w-full flex justify-center items-center max-h-[80vh] mx-auto"
                  >
                    {/* Single image */}
                    {item.image.length === 1 && (
                      <img
                        src={item.image[0]}
                        alt={`${t(item.titleKey ?? `feature_${item.id}_title`)} - Flateroo feature`}
                        className="h-full w-auto object-contain transition-transform duration-700 hover:scale-[1.03] max-h-[65vh] drop-shadow-2xl"
                        draggable={false}
                      />
                    )}

                    {/* Stacked two-image layout */}
                    {item.image.length === 2 && (
                      <div className="relative w-full h-[50vh] md:h-[60vh] flex items-center justify-center">
                        {/* Back image (left, rotated) */}
                        <motion.img
                          src={item.image[1]}
                          alt={`${item.id} back`}
                          initial={{
                            opacity: 0,
                            rotate: 0,
                            x: "-50%",
                            y: "-50%",
                          }}
                          whileInView={{
                            opacity: 1,
                            rotate: -7,
                            x: "-90%",
                            y: "-50%",
                          }}
                          transition={{
                            duration: 0.8,
                            delay: 0.3,
                            ease: [0.16, 1, 0.3, 1] as [
                              number,
                              number,
                              number,
                              number,
                            ],
                          }}
                          viewport={{ once: true, margin: "-15%" }}
                          className="absolute left-1/2 top-1/2 h-[45vh] md:h-[50vh] max-w-[80vw] w-auto object-contain drop-shadow-2xl z-[1]"
                          draggable={false}
                        />

                        {/* Front image (right, rotated) */}
                        <motion.img
                          src={item.image[0]}
                          alt={`${item.id} front`}
                          initial={{
                            opacity: 0,
                            rotate: 0,
                            x: "-50%",
                            y: "-50%",
                          }}
                          whileInView={{
                            opacity: 1,
                            rotate: 7,
                            x: "-10%",
                            y: "-50%",
                          }}
                          transition={{
                            duration: 0.8,
                            delay: 0.45,
                            ease: [0.16, 1, 0.3, 1] as [
                              number,
                              number,
                              number,
                              number,
                            ],
                          }}
                          viewport={{ once: true, margin: "-15%" }}
                          className="absolute left-1/2 top-1/2 h-[45vh] md:h-[50vh] max-w-[80vw] w-auto object-contain drop-shadow-2xl z-[2]"
                          draggable={false}
                        />
                      </div>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
